import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import { routeTree } from "./routeTree.gen"
import "./styles/globals.css"
import env from "$/lib/env.ts"
import { TokenRefreshUnavailableError } from "$/lib/errors.ts"
import { readDeviceLanguage } from "$/lib/language-preference"
import translationQueries from "$/lib/queries/translation-queries"
import { userQueryOptions } from "$/lib/queries/user-queries"
import { setupChunkErrorHandler } from "$/lib/utils/chunk-error-handler"
import { STOCKHOLM_TIME_ZONE, TranslationKey, Translator } from "@repo/common"
import { TooltipProvider } from "@repo/ui/components/ui/tooltip"
import { toast } from "@repo/ui/hooks/use-toast"
import * as Sentry from "@sentry/react"
import { captureException, withScope } from "@sentry/react"
import { RowData } from "@tanstack/react-table"
import { isAxiosError } from "axios"
import { createTranslator } from "use-intl"
import Default404 from "./components/404"
import { renderConnectedJobsGroupReceiptDialog } from "./components/combined-receipt/combined-receipt-button"
import { renderEditHandInDateDialog } from "./components/edit-hand-in-date-dialog"
import { RouteErrorComponent } from "./components/route-error-component"
import { GroupReceiptDialogRendererProvider } from "./components/universal-job-card/group-receipt-dialog-context"
import { HandInDateDialogRendererProvider } from "./components/universal-job-card/hand-in-date-dialog-context"
import { JobHoverDataFetcherProvider } from "./components/universal-job-card/hover-data-context"
import { fetchJobHover } from "./lib/queries/job-hover-queries"

// Setup chunk error handler before anything else
setupChunkErrorHandler()

if (!env.VITE_API_URL.includes("localhost")) {
  Sentry.init({
    dsn: "https://f820141f6601e03392267e54e80a153c@o4509082431848448.ingest.de.sentry.io/4509082434338896",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        // These request/response bodies carry credentials: the password and
        // the refresh token. identitytoolkit is where the Firebase SDK's
        // signInWithEmailAndPassword posts; securetoken is the refresh.
        networkDetailAllowUrls: [
          /^(?!.*(identitytoolkit\.googleapis\.com|securetoken\.googleapis\.com)).*$/,
        ],
      }),
      Sentry.extraErrorDataIntegration(),
    ],
    // Session Replay
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error (buffer mode)
    tracePropagationTargets: ["https://nest-prod-api-yysmrjg6aa-lz.a.run.app"],
    tracesSampleRate: 1.0,
  })
}

const DEFAULT_QUERY_RETRIES = 3

// For the caches' handlers, which run outside the TranslationProvider. The
// root route's loader has already put the dictionary in the cache.
const translateOutsideReact = (key: TranslationKey): string => {
  const user = queryClient.getQueryData(userQueryOptions.queryKey)
  const language = user?.language ?? readDeviceLanguage()
  const dictionary = queryClient.getQueryData(
    translationQueries.dictionary(language).queryKey,
  )
  if (!dictionary) return key
  const t: Translator = createTranslator({
    locale: language,
    messages: dictionary.messages,
    timeZone: STOCKHOLM_TIME_ZONE,
  })
  return t(key)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth failures are owned by the api interceptor, which refreshes and
      // replays once. Retrying them here only burns three more requests with
      // the same rejected token.
      retry: (failureCount, error) => {
        if (isAxiosError(error)) {
          const status = error.response?.status
          if (status === 401 || status === 403) return false
        }
        return failureCount < DEFAULT_QUERY_RETRIES
      },
    },
  },
  mutationCache: new MutationCache({
    onError: async (err, _variables, _context, mutation) => {
      if (
        isAxiosError(err) &&
        err.response?.status === 500 &&
        typeof err.response?.data?.message === "string" &&
        err.response.data.message.includes("Failed to update order in FDT") &&
        err.response.data.message.includes("404")
      ) {
        toast({
          title: translateOutsideReact(
            "alfred.lib.mutationErrors.fdtOrderNotFound",
          ),
          variant: "destructive",
        })
        return
      }

      // The token source already reports this once per incident; reporting
      // it again here would add an event per failed mutation.
      if (err instanceof TokenRefreshUnavailableError) return

      withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          variables: mutation.state.variables,
        })
        if (mutation.options.mutationKey) {
          scope.setFingerprint(
            // Duplicate to prevent modification
            Array.from(mutation.options.mutationKey) as string[],
          )
        }
        captureException(err)
      })
    },
  }),
  queryCache: new QueryCache({
    onError: async (err, query) => {
      // The token source already reports this once per incident; reporting
      // it again here would open a Sentry group per query. The user gets the
      // error screen and its retry button.
      if (err instanceof TokenRefreshUnavailableError) return

      withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash })
        scope.setFingerprint([query.queryHash.replace(/[0-9]/g, "0")])
        captureException(err)
      })
    },
  }),
})

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  defaultNotFoundComponent: () => <Default404 />,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

declare module "@tanstack/history" {
  interface HistoryState {
    // Marks the navigation the store switcher starts when leaving a job
    switchToStoreId?: string
  }
}

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string
    sticky?: boolean
  }
}

const rootElement = document.getElementById("app")!

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <JobHoverDataFetcherProvider value={fetchJobHover}>
          <GroupReceiptDialogRendererProvider
            value={renderConnectedJobsGroupReceiptDialog}
          >
            <HandInDateDialogRendererProvider
              value={renderEditHandInDateDialog}
            >
              <TooltipProvider>
                <RouterProvider router={router} />
              </TooltipProvider>
            </HandInDateDialogRendererProvider>
          </GroupReceiptDialogRendererProvider>
        </JobHoverDataFetcherProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}
